import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useApp } from "@/store/useApp";
import type { Product, CutType, Unit } from "@/lib/types";
import { Minus, Plus, Lock, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  resolveProductPriceQuote,
  offerableCutsForUnit,
  offerableUnits,
} from "@/lib/catalogPricing";

interface Props {
  product: Product | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ProductOfferModal({ product, open, onOpenChange }: Props) {
  const { account, addToCart, catalogUnits } = useApp();
  const validated = account?.status === "VALIDADO";

  const [cut, setCut] = useState<CutType>("");
  const [unit, setUnit] = useState<Unit>("KG");
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState("");

  // Cortes ofertáveis (match exato OU deriváveis do KG do corte) para a unidade — só quando validado.
  // Sem validação (cotação), todas as opções ficam disponíveis pois não há preço para filtrar.
  const validCutsForUnit = (unitName: string, allCuts: CutType[]): CutType[] => {
    if (!validated || !product) return allCuts;
    return offerableCutsForUnit(product, unitName, catalogUnits);
  };

  // Primeiro corte válido para a unidade (prefere um corte nomeado ofertável; senão "" = base/sem corte).
  const firstValidCut = (unitName: string, allCuts: CutType[]): CutType => {
    if (!validated || !product) return allCuts[0] ?? "";
    const named = offerableCutsForUnit(product, unitName, catalogUnits);
    if (named.length > 0) return named[0];
    // Sem corte nomeado ofertável: usa base ("") se a combinação sem corte estiver disponível.
    return resolveProductPriceQuote(product, unitName, catalogUnits, 1, undefined).available ? "" : (allCuts[0] ?? "");
  };

  const firstValidUnit = (allUnits: Unit[], preferred: Unit): Unit => {
    if (!validated || !product) return preferred;
    const offerable = offerableUnits(product, catalogUnits);
    if (offerable.includes(preferred)) return preferred;
    return offerable[0] ?? preferred;
  };

  useEffect(() => {
    if (product) {
      const allUnits =
        product.availableUnits && product.availableUnits.length > 0
          ? product.availableUnits
          : [product.defaultUnit];
      const allCuts = product.availableCuts ?? [];
      const initialUnit = firstValidUnit(allUnits, product.defaultUnit);
      setUnit(initialUnit);
      setCut(firstValidCut(initialUnit, allCuts));
      setQuantity(1);
      setNotes("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, validated, catalogUnits]);

  if (!product) return null;

  const cuts = product.availableCuts ?? [];
  const allUnits =
    product.availableUnits && product.availableUnits.length > 0
      ? product.availableUnits
      : [product.defaultUnit];

  // Unidades e cortes oferecidos: quando validado, só as combinações disponíveis (exatas ou deriváveis do KG).
  const units = validated ? offerableUnits(product, catalogUnits) : allUnits;
  const displayCuts = validCutsForUnit(unit, cuts);

  const priceQuote = validated ? resolveProductPriceQuote(product, unit, catalogUnits, quantity, cut) : undefined;
  const pricePerUnit = priceQuote?.pricePerUnit;
  const available = validated ? (priceQuote?.available ?? false) : true;
  const surchargeApplies = validated && (priceQuote?.surchargeApplied ?? false);
  const estimatedTotal = pricePerUnit != null ? pricePerUnit * quantity : undefined;

  const selectUnit = (u: Unit) => {
    setUnit(u);
    // Ao trocar de unidade, garante que o corte selecionado continua válido (com preço) para ela.
    if (!validCutsForUnit(u, cuts).includes(cut)) {
      setCut(firstValidCut(u, cuts));
    }
  };

  const confirmAdd = () => {
    // Não envia preço otimista: o backend é a fonte de verdade e reprecifica o carrinho no PUT.
    void addToCart({
      productId: product.id,
      productName: product.name.toUpperCase(),
      emoji: product.emoji,
      category: product.category,
      cut: cut || "",
      unit,
      quantity,
      notes: notes || undefined,
    });
    toast.success(`${product.name} adicionado ao carrinho`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden bg-background">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border bg-surface-elevated">
          <div className="flex items-start gap-4">
            <div className="h-16 w-16 rounded-xl bg-secondary flex items-center justify-center text-4xl shrink-0">
              {product.emoji}
            </div>
            <div className="flex-1 min-w-0 pt-1">
              <DialogTitle className="font-display text-xl uppercase tracking-tight">
                {product.name}
              </DialogTitle>
              <p className="text-xs font-medium text-muted-foreground mt-1 tracking-wider">
                {product.category}{product.isProcessed && " · PROCESSADO"}
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="px-6 py-5 space-y-5 max-h-[60vh] overflow-y-auto">
          {!validated && (
            <div className="rounded-lg border border-warning/30 bg-warning/5 p-3 flex items-start gap-2.5">
              <Lock className="h-4 w-4 text-warning mt-0.5 shrink-0" />
              <div className="text-xs">
                <p className="font-semibold text-foreground">Preço sob aprovação comercial</p>
                <p className="text-muted-foreground mt-0.5">
                  Configure o item normalmente. O valor é liberado quando sua ficha comercial é validada.
                </p>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-sm font-semibold">
              Unidade <span className="text-destructive">*</span>
            </Label>
            <div className="grid grid-cols-4 gap-2">
              {units.map((u) => (
                <button
                  key={u}
                  type="button"
                  onClick={() => selectUnit(u)}
                  className={cn(
                    "h-10 rounded-lg border text-sm font-medium transition-all",
                    unit === u
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-surface hover:border-primary/40",
                  )}
                >
                  {u}
                </button>
              ))}
            </div>
          </div>

          {displayCuts.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm font-semibold">
                Tipo de corte <span className="text-destructive">*</span>
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {displayCuts.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCut(c)}
                    className={cn(
                      "h-10 rounded-lg border text-sm font-medium transition-all flex items-center justify-center gap-1.5",
                      cut === c
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-surface hover:border-primary/40",
                    )}
                  >
                    {cut === c && <Check className="h-3.5 w-3.5" />}
                    {c}
                  </button>
                ))}
              </div>
              {validated && (
                <p className="text-[11px] text-muted-foreground">
                  Apenas cortes com preço para a unidade selecionada são exibidos.
                </p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-sm font-semibold">Quantidade</Label>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setQuantity((q) => Math.max(0.1, +(q - 1).toFixed(1)))}
              >
                <Minus className="h-4 w-4" />
              </Button>
              <Input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(+e.target.value || 0)}
                step="0.1"
                min="0.1"
                className="text-center font-display text-lg font-semibold tabular-nums h-11"
              />
              <Button variant="outline" size="icon" onClick={() => setQuantity((q) => +(q + 1).toFixed(1))}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-success font-medium pt-1">
              {quantity} {unit} de {product.name}
              {cut ? ` — ${cut}` : ""}
            </p>
            {surchargeApplies && (
              <p className="text-xs text-warning font-medium">
                Embalagem inferior a 1 kg: acréscimo de 10% sobre o valor do kg.
              </p>
            )}
            {validated && !available && (
              <p className="text-xs text-destructive font-medium">
                Combinação indisponível — não há preço cadastrado para esta unidade e corte.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes" className="text-sm font-semibold">
              Observações do item <span className="text-muted-foreground font-normal">(opcional)</span>
            </Label>
            <Textarea
              id="notes"
              placeholder="ex: bem maduro, sem manchas, embalar separado..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-border bg-surface-elevated flex items-center justify-between gap-3">
          {validated && estimatedTotal != null && (
            <div>
              <p className="text-xs text-muted-foreground">
                Total estimado{surchargeApplies ? " (com acréscimo de 10%)" : ""}
              </p>
              <p className="font-display text-xl font-bold tabular-nums">
                R$ {estimatedTotal.toFixed(2)}
              </p>
            </div>
          )}
          <Button variant="hero" size="lg" onClick={confirmAdd} disabled={validated && !available}>
            Adicionar ao carrinho
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
